import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateTime, formatPhone } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useRedemptions } from './promo-codes.api';

/**
 * Who has used a code.
 *
 * The one place the portal can answer "has this been redeemed", since the
 * search response's `isRedeemed` is never populated — see the note in ./types.
 */
export function RedemptionsDialog({
  code,
  open,
  onOpenChange,
}: {
  code: { id: string; code: string | null } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const list = useRedemptions(open ? (code?.id ?? null) : null);

  const rows = list.data ?? [];
  // `isLoading`, not `isPending`: the query is disabled while the dialog is
  // closed. Reopening a code whose list went stale re-reads it in place.
  const loading = list.isLoading;
  const busy = list.isFetching && !loading;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('promoCodes.redemptions')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('promoCodes.redemptionsFor', { code: code?.code ?? '' })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {loading && (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-12 w-full rounded-lg" />
            ))}
          </div>
        )}

        {list.isError && (
          <p className="text-destructive text-sm">
            {t('promoCodes.redemptionsFailed')}
          </p>
        )}

        {list.isSuccess && rows.length === 0 && (
          <div className="relative rounded-xl">
            <BusyOverlay show={busy} />
            <p className="text-muted-foreground rounded-xl border border-dashed py-8 text-center text-sm">
              {t('promoCodes.neverRedeemed')}
            </p>
          </div>
        )}

        {rows.length > 0 && (
          <div className="relative rounded-xl">
            <BusyOverlay show={busy} />
            <div
              aria-busy={busy}
              className={cn(
                'max-h-80 divide-y overflow-y-auto rounded-xl border',
                busy && 'opacity-60 transition-opacity'
              )}
            >
              {rows.map((row, index) => (
                <div
                  // Redemptions carry no id of their own, and one customer can
                  // appear twice, so position is the only stable key here.
                  key={`${row.confirmationCode}-${index}`}
                  className="space-y-0.5 p-3 text-sm"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="truncate font-medium">
                      {[row.customerFirstName, row.customerLastName]
                        .filter(Boolean)
                        .join(' ') || '—'}
                    </p>
                    <p className="text-muted-foreground shrink-0 text-xs">
                      {formatDateTime(row.createdAt)}
                    </p>
                  </div>
                  <p className="text-muted-foreground truncate text-xs">
                    {row.customerEmail}
                    {row.customerPhoneNumber &&
                      ` · ${formatPhone(row.customerPhoneNumber)}`}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {row.businessName}
                    {row.confirmationCode && (
                      <span className="font-mono"> · {row.confirmationCode}</span>
                    )}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogAction>{t('promoCodes.close')}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
